import { auth } from '../firebase';
import { API_BASE_URL } from '../config';

const IMAGE_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp'
]);

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for PDF conversion.'));
    img.src = src;
  });

const fileToBase64 = async (file) => {
  const dataUrl = await readFileAsDataUrl(file);
  return String(dataUrl).split(',')[1] || '';
};

const buildImagePreviewPayload = async (file) => {
  const sourceDataUrl = await readFileAsDataUrl(file);
  const image = await loadImage(sourceDataUrl);

  const maxDimension = 1200;
  const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Cannot prepare image preview for AI arrangement.');

  ctx.drawImage(image, 0, 0, width, height);
  const previewDataUrl = canvas.toDataURL('image/jpeg', 0.72);

  return {
    name: file.name,
    mimeType: 'image/jpeg',
    base64Data: previewDataUrl.split(',')[1] || '',
  };
};

const renderImageOnPdfPage = (pdf, imageDataUrl, image, mimeType) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const scale = Math.min(pageWidth / image.width, pageHeight / image.height);
  const renderWidth = image.width * scale;
  const renderHeight = image.height * scale;
  const offsetX = (pageWidth - renderWidth) / 2;
  const offsetY = (pageHeight - renderHeight) / 2;
  const imageFormat = mimeType === 'image/png' ? 'PNG' : 'JPEG';

  pdf.addImage(imageDataUrl, imageFormat, offsetX, offsetY, renderWidth, renderHeight, undefined, 'FAST');
};

const imageToPdfFile = async (file) => {
  const { jsPDF } = await import('jspdf');
  const imageDataUrl = await readFileAsDataUrl(file);
  const image = await loadImage(imageDataUrl);

  const orientation = image.width > image.height ? 'landscape' : 'portrait';
  const pdf = new jsPDF({ orientation, unit: 'mm', format: 'a4' });

  renderImageOnPdfPage(pdf, imageDataUrl, image, file.type);

  const pdfBlob = pdf.output('blob');
  const baseName = file.name.replace(/\.[^/.]+$/, '');
  return new File([pdfBlob], `${baseName}.pdf`, {
    type: 'application/pdf'
  });
};

const imagesToSinglePdfFile = async (files) => {
  if (!Array.isArray(files) || files.length === 0) {
    throw new Error('No image files were provided.');
  }

  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    const imageDataUrl = await readFileAsDataUrl(file);
    const image = await loadImage(imageDataUrl);

    if (index > 0) {
      pdf.addPage('a4', 'portrait');
    }

    renderImageOnPdfPage(pdf, imageDataUrl, image, file.type);
  }

  const pdfBlob = pdf.output('blob');
  const firstBaseName = files[0].name.replace(/\.[^/.]+$/, '');
  const mergedName = `${firstBaseName}_stack_${files.length}.pdf`;
  return new File([pdfBlob], mergedName, {
    type: 'application/pdf'
  });
};

const prepareUploadFile = async (file) => {
  if (!file) throw new Error('No file selected for upload.');
  if (file.type === 'application/pdf') return file;
  if (IMAGE_MIME_TYPES.has(file.type)) {
    return imageToPdfFile(file);
  }
  throw new Error('Only PDF, PNG, JPG, or WEBP files are supported.');
};

const getScanAuthToken = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Not signed in.');
  return currentUser.getIdToken(true);
};

const requestAiImageArrangement = async ({ files, idToken, signal }) => {
  const images = await Promise.all(files.map((file) => buildImagePreviewPayload(file)));

  let response;
  try {
    response = await fetch(`${API_BASE_URL}/api/documents/arrange-images`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ images }),
      signal,
    });
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(`Cannot connect to backend at ${API_BASE_URL}. Make sure avisa-backend is running on port 5000.`);
    }
    throw error;
  }

  const payload = await response.json();
  if (!response.ok) {
    const retryAfter = Number(payload?.retryAfterSeconds);
    const retryHint = Number.isFinite(retryAfter) && retryAfter > 0
      ? ` Retry after ${retryAfter}s.`
      : '';
    throw new Error((payload.error || 'Failed to arrange images with AI') + retryHint);
  }

  return {
    orderedRelevantIndexes: Array.isArray(payload.orderedRelevantIndexes) ? payload.orderedRelevantIndexes : [],
    excludedIndexes: Array.isArray(payload.excludedIndexes) ? payload.excludedIndexes : [],
    summary: typeof payload.summary === 'string' ? payload.summary : '',
  };
};

const submitScanFile = async ({ uploadFile, idToken, signal, onUploadProgress, dryRun = false }) => {
  const formData = new FormData();
  formData.append('file', uploadFile, uploadFile.name);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    const abortUpload = () => xhr.abort();
    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Upload aborted.', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', abortUpload, { once: true });
    }

    const url = `${API_BASE_URL}/api/documents/scan${dryRun ? '?dryRun=true' : ''}`;
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${idToken}`);

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || typeof onUploadProgress !== 'function') return;
      onUploadProgress({
        loaded: event.loaded,
        total: event.total,
        percent: event.total > 0 ? Math.round((event.loaded / event.total) * 100) : 0,
        fileName: uploadFile.name,
      });
    };

    xhr.upload.onload = () => {
      if (typeof onUploadProgress !== 'function') return;
      onUploadProgress({
        loaded: uploadFile.size || 0,
        total: uploadFile.size || 0,
        percent: 100,
        fileName: uploadFile.name,
        uploadComplete: true,
      });
    };

    xhr.onload = () => {
      signal?.removeEventListener('abort', abortUpload);
      const payload = (() => {
        try {
          return JSON.parse(xhr.responseText || '{}');
        } catch {
          return {};
        }
      })();

      if (xhr.status < 200 || xhr.status >= 300) {
        const retryAfter = Number(payload?.retryAfterSeconds);
        const retryHint = Number.isFinite(retryAfter) && retryAfter > 0
          ? ` Retry after ${retryAfter}s.`
          : '';
        reject(new Error((payload.error || 'Failed to scan document') + retryHint));
        return;
      }

      resolve(payload);
    };

    xhr.onerror = () => {
      signal?.removeEventListener('abort', abortUpload);
      reject(new Error(`Cannot connect to backend at ${API_BASE_URL}. Make sure avisa-backend is running on port 5000.`));
    };

    xhr.onabort = () => {
      signal?.removeEventListener('abort', abortUpload);
      reject(new DOMException('Upload aborted.', 'AbortError'));
    };

    xhr.send(formData);
  });
};

const calculateUploadedProgress = ({ completedBytes, currentLoaded, currentTotal, totalBytes }) => {
  const effectiveTotal = totalBytes || currentTotal || 1;
  const loaded = Math.min(effectiveTotal, completedBytes + currentLoaded);
  return {
    loaded,
    total: effectiveTotal,
    percent: Math.round((loaded / effectiveTotal) * 100),
  };
};

const prepareUploadFiles = async (selectedFiles, onProgress) => {
  const preparedFiles = [];
  for (let index = 0; index < selectedFiles.length; index += 1) {
    onProgress?.({
      phase: 'preparing',
      fileName: selectedFiles[index]?.name || '',
      fileIndex: index,
      fileCount: selectedFiles.length,
    });
    preparedFiles.push(await prepareUploadFile(selectedFiles[index]));
  }
  return preparedFiles;
};

const createAggregateUploadProgressHandler = ({
  completedBytes,
  uploadFile,
  uploadFileSize,
  fileIndex,
  fileCount,
  totalBytes,
  onProgress,
}) => (progress) => {
  const currentLoaded = progress.uploadComplete
    ? uploadFileSize
    : progress.total > 0 && uploadFileSize > 0
      ? Math.round((progress.loaded / progress.total) * uploadFileSize)
      : progress.loaded;
  const aggregate = calculateUploadedProgress({
    completedBytes,
    currentLoaded,
    currentTotal: uploadFileSize || progress.total || 0,
    totalBytes,
  });
  onProgress?.({
    ...aggregate,
    phase: progress.uploadComplete
      ? fileIndex === fileCount - 1
        ? 'processing'
        : 'processing-current'
      : 'uploading',
    fileName: uploadFile.name,
    fileIndex,
    fileCount,
  });
};

/**
 * Sends a document file to the backend for scanning with Gemini.
 * @param {File} file 
 * @returns {Promise<Object>}
 */
export const scanDocument = async (file, signal, onProgress) => {
  const idToken = await getScanAuthToken();
  const uploadFile = await prepareUploadFile(file);
  return submitScanFile({ uploadFile, idToken, signal, onUploadProgress: onProgress });
};

/**
 * Scans multiple files. If all selected files are images, they are merged
 * into one multi-page PDF and uploaded as a single scan request.
 * @param {File[]} files
 * @returns {Promise<{results: Object[], uploadMode: string, sourceCount: number, uploadedCount: number}>}
 */
export const scanDocumentBatch = async (files, signal, onProgress) => {
  const selectedFiles = Array.isArray(files) ? files.filter(Boolean) : [];
  if (selectedFiles.length === 0) {
    throw new Error('No file selected for upload.');
  }

  const idToken = await getScanAuthToken();
  const allAreImages = selectedFiles.every((file) => IMAGE_MIME_TYPES.has(file.type));
  onProgress?.({ phase: 'preparing', percent: 0, fileCount: selectedFiles.length });

  if (allAreImages) {
    const arrangement = await requestAiImageArrangement({ files: selectedFiles, idToken, signal });
    const orderedImages = arrangement.orderedRelevantIndexes
      .map((index) => selectedFiles[index])
      .filter(Boolean);

    if (orderedImages.length === 0) {
      throw new Error('AI assistant did not find ordinance-relevant pages in this image set.');
    }

    const mergedPdf = await imagesToSinglePdfFile(orderedImages);
    const payload = await submitScanFile({
      uploadFile: mergedPdf,
      idToken,
      signal,
      dryRun: true,
      onUploadProgress: (progress) => onProgress?.({
        ...progress,
        phase: progress.uploadComplete ? 'processing' : 'uploading',
        fileIndex: 0,
        fileCount: 1,
      }),
    });
    onProgress?.({ phase: 'processing', percent: 100, loaded: mergedPdf.size, total: mergedPdf.size });
    return {
      results: [payload],
      uploadMode: selectedFiles.length > 1 ? 'merged-image-stack' : 'single-image',
      sourceCount: selectedFiles.length,
      uploadedCount: 1,
      keptCount: orderedImages.length,
      excludedCount: arrangement.excludedIndexes.length,
      excludedIndexes: arrangement.excludedIndexes,
      arrangementSummary: arrangement.summary,
    };
  }

  const preparedFiles = await prepareUploadFiles(selectedFiles, onProgress);
  const totalBytes = preparedFiles.reduce((sum, file) => sum + (file.size || 0), 0);
  let completedBytes = 0;
  const results = [];
  for (let index = 0; index < preparedFiles.length; index += 1) {
    const uploadFile = preparedFiles[index];
    const uploadFileSize = uploadFile.size || 0;
    const payload = await submitScanFile({
      uploadFile,
      idToken,
      signal,
      dryRun: true,
      onUploadProgress: createAggregateUploadProgressHandler({
        completedBytes,
        uploadFile,
        uploadFileSize,
        fileIndex: index,
        fileCount: preparedFiles.length,
        totalBytes,
        onProgress,
      }),
    });
    results.push(payload);
    completedBytes += uploadFile.size || 0;
  }
  onProgress?.({ phase: 'processing', percent: 100, loaded: totalBytes, total: totalBytes });

  return {
    results,
    uploadMode: selectedFiles.length > 1 ? 'multi-file' : 'single-file',
    sourceCount: selectedFiles.length,
    uploadedCount: results.length,
  };
};

/**
 * Fetches all analyzed documents from Firestore via the backend.
 * @returns {Promise<Array>}
 */
export const fetchDocuments = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Not signed in.');

  const idToken = await currentUser.getIdToken(true);
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/api/documents`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(`Cannot connect to backend at ${API_BASE_URL}. Make sure avisa-backend is running on port 5000.`);
    }
    throw error;
  }

  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload.error || 'Failed to fetch documents');
  }

  return payload.documents || [];
};

/**
 * Deletes an analyzed document from Firestore via the backend.
 * @param {string} id 
 * @returns {Promise<Object>}
 */
export const deleteDocument = async (id) => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Not signed in.');

  const idToken = await currentUser.getIdToken(true);
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/api/documents/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(`Cannot connect to backend at ${API_BASE_URL}. Make sure avisa-backend is running on port 5000.`);
    }
    throw error;
  }

  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload.error || 'Failed to delete document');
  }

  return payload;
};

/**
 * Triggers backend AI translation to Filipino for an existing document.
 * @param {string} id
 * @returns {Promise<Object>}
 */
export const translateDocumentFilipino = async (id, options = {}) => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Not signed in.');

  const idToken = await currentUser.getIdToken(true);
  const force = options?.force === true;
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/api/documents/${id}/translate-filipino`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ force }),
    });
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(`Cannot connect to backend at ${API_BASE_URL}. Make sure avisa-backend is running on port 5000.`);
    }
    throw error;
  }

  const payload = await response.json();
  if (!response.ok) {
    const retryAfter = Number(payload?.retryAfterSeconds);
    const retryHint = Number.isFinite(retryAfter) && retryAfter > 0
      ? ` Retry after ${retryAfter}s.`
      : '';
    throw new Error((payload.error || 'Failed to translate document') + retryHint);
  }

  return payload;
};

/**
 * Fetches Filipino translation from documents_translations via backend.
 * @param {string} id
 * @returns {Promise<Object>}
 */
export const fetchDocumentFilipinoTranslation = async (id) => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Not signed in.');

  const idToken = await currentUser.getIdToken(true);
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/api/documents/${id}/translate-filipino`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(`Cannot connect to backend at ${API_BASE_URL}. Make sure avisa-backend is running on port 5000.`);
    }
    throw error;
  }

  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload.error || 'Failed to fetch document translation');
  }

  return payload;
};

/**
 * Updates AI Analysis Summary (summary and keyPoints) for a document
 * @param {string} id - Document ID
 * @param {Object} updates - Update object containing:
 *   - summary (string): English summary
 *   - keyPoints (array): English key points
 *   - filipinoSummary (string): Filipino summary
 *   - filipinoKeyPoints (array): Filipino key points
 *   - title (string): Full title of the document
 *   - ordinanceNumber (string): Ordinance number
 *   - legalLevel (string): Scope/Level
 * @returns {Promise<Object>}
 */
export const updateDocumentAnalysisSummary = async (id, updates) => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Not signed in.');

  const idToken = await currentUser.getIdToken(true);
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/api/documents/${id}/analysis-summary`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(`Cannot connect to backend at ${API_BASE_URL}. Make sure avisa-backend is running on port 5000.`);
    }
    throw error;
  }

  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload.error || 'Failed to update document analysis summary');
  }

  return payload;
};

export const confirmDocuments = async (documents) => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Not signed in.');

  const idToken = await currentUser.getIdToken(true);
  
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/api/documents/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ documents }),
    });
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(`Cannot connect to backend at ${API_BASE_URL}. Make sure avisa-backend is running.`);
    }
    throw error;
  }

  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload.error || 'Failed to save documents');
  }

  return payload;
};

export const normalizeOrdinanceNumberForMatch = (str) => {
  if (!str || typeof str !== 'string') return '';
  let s = str.toLowerCase().trim();
  if (['n/a', 'none', 'null', 'undefined', 'not applicable', 'unspecified'].includes(s)) return '';
  s = s.replace(/^(republic\s*act|ra|ordinance|kautusan|kautusang\s*pambarangay|resolution|resolusyon|executive\s*order|eo|batas\s*pambansa|bp)\s*(no\.?|number|blg\.?)?/gi, '');
  s = s.replace(/^(no\.?|blg\.?)\s*/gi, '');
  s = s.replace(/series\s*of\s*(?:19|20)\d{2}/gi, '');
  s = s.replace(/s\.\s*(?:19|20)\d{2}/gi, '');
  s = s.replace(/[^a-z0-9]/gi, '');
  return s;
};

export const normalizeTitleForMatch = (str) => {
  if (!str || typeof str !== 'string') return '';
  let s = str.toLowerCase().trim();
  s = s.replace(/[^a-z0-9\s]/gi, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  return s;
};

export const checkDocumentDuplicate = async (candidateDoc, excludeDocId = null) => {
  const currentUser = auth.currentUser;
  if (!currentUser) return { ok: true, isDuplicate: false };

  try {
    const idToken = await currentUser.getIdToken(true);
    const response = await fetch(`${API_BASE_URL}/api/documents/check-duplicate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ candidateDoc, excludeDocId }),
    });
    if (!response.ok) return { ok: false, isDuplicate: false };
    return await response.json();
  } catch (err) {
    console.warn('checkDocumentDuplicate error:', err);
    return { ok: false, isDuplicate: false };
  }
};


